export enum TimeZone {
  ASIA_TAIPEI = "Asia/Taipei",
  ASIA_TOKYO = "Asia/Tokyo",
  ASIA_BANGKOK = "Asia/Bangkok",
  ASIA_HOCHIMINH = "Asia/Ho_Chi_Minh", // Hanoi uses the same time zone as Ho Chi Minh City
  ASIA_HONGKONG = "Asia/Hong_Kong",
  ASIA_SINGAPORE = "Asia/Singapore",
  ASIA_MANILA = "Asia/Manila",
  ASIA_SEOUL = "Asia/Seoul",
  AMERICA_NEW_YORK = "America/New_York",
  AMERICA_CHICAGO = "America/Chicago",
  AMERICA_LOS_ANGELES = "America/Los_Angeles",
}

export function formatDate(dateTimeStr: string, timeZone: TimeZone): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: timeZone,
  }).format(new Date(dateTimeStr));
}

export function formatTime(dateTimeStr: string, timeZone: TimeZone): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    timeZone: timeZone,
  }).format(new Date(dateTimeStr));
}

export function formatDateTimeDelta(dateTime: Date): string {
  const now = new Date();
  const delta = Math.floor((now.getTime() - dateTime.getTime()) / 1000); // in seconds

  const SECONDS_IN_A_MINUTE = 60;
  const SECONDS_IN_AN_HOUR = 3600;
  const SECONDS_IN_A_DAY = 86400;
  const SECONDS_IN_A_WEEK = 7 * SECONDS_IN_A_DAY;
  const SECONDS_IN_A_MONTH = 30 * SECONDS_IN_A_DAY;
  const SECONDS_IN_A_YEAR = 365 * SECONDS_IN_A_DAY;

  const years = Math.floor(delta / SECONDS_IN_A_YEAR);
  const months = Math.floor((delta % SECONDS_IN_A_YEAR) / SECONDS_IN_A_MONTH);
  const weeks = Math.floor((delta % SECONDS_IN_A_MONTH) / SECONDS_IN_A_WEEK);
  const days = Math.floor((delta % SECONDS_IN_A_WEEK) / SECONDS_IN_A_DAY);
  const hours = Math.floor((delta % SECONDS_IN_A_DAY) / SECONDS_IN_AN_HOUR);
  const minutes = Math.floor(
    (delta % SECONDS_IN_AN_HOUR) / SECONDS_IN_A_MINUTE,
  );

  const timeUnits = [
    { value: years, label: "yr" },
    { value: months, label: "mo" },
    { value: weeks, label: "w" },
    { value: days, label: "d" },
    { value: hours, label: "h" },
    { value: minutes, label: "m" },
  ];
  const unit = timeUnits.find(({ value }) => value > 0); // find the first non-zero unit
  return unit ? `${unit.value}${unit.label}` : "just now";
}

export function formatTimeRemaining(
  from: Date,
  to: Date,
  showSeconds = false,
): string {
  const delta = Math.floor((to.getTime() - from.getTime()) / 1000); // in seconds

  if (delta <= 0) {
    return showSeconds ? "0s" : "0m";
  }

  const SECONDS_IN_A_MINUTE = 60;
  const SECONDS_IN_AN_HOUR = 3600;
  const SECONDS_IN_A_DAY = 86400;

  const days = Math.floor(delta / SECONDS_IN_A_DAY);
  const hours = Math.floor((delta % SECONDS_IN_A_DAY) / SECONDS_IN_AN_HOUR);
  const minutes = Math.floor(
    (delta % SECONDS_IN_AN_HOUR) / SECONDS_IN_A_MINUTE,
  );
  const seconds = Math.floor(delta % SECONDS_IN_A_MINUTE);

  if (days > 0) {
    return showSeconds
      ? `${days}d ${hours}h ${minutes}m ${seconds}s`
      : `${days}d ${hours}h`;
  } else if (hours > 0) {
    return showSeconds
      ? `${hours}h ${minutes}m ${seconds}s`
      : `${hours}h ${minutes}m`;
  } else if (minutes > 0 || !showSeconds) {
    return showSeconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
  } else {
    return `${seconds}s`;
  }
}

export function isExpired(expirationDate: Date): boolean {
  return new Date() > expirationDate;
}

export function getHoursMinutesSecondsDelta(
  to: Date,
  from: Date,
): {
  totalHours: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
} {
  const delta = Math.floor((to.getTime() - from.getTime()) / 1000); // in seconds

  const SECONDS_IN_A_MINUTE = 60;
  const SECONDS_IN_AN_HOUR = 3600;
  const SECONDS_IN_A_DAY = 86400;

  // For negative deltas, we need to handle the division and remainder differently
  const sign = delta < 0 ? -1 : 1;
  const absDelta = Math.abs(delta);

  const totalHours = sign * Math.floor(absDelta / SECONDS_IN_AN_HOUR);
  const days = sign * Math.floor(absDelta / SECONDS_IN_A_DAY);
  const hours =
    sign * Math.floor((absDelta % SECONDS_IN_A_DAY) / SECONDS_IN_AN_HOUR);
  const minutes =
    sign * Math.floor((absDelta % SECONDS_IN_AN_HOUR) / SECONDS_IN_A_MINUTE);
  const seconds = sign * Math.floor(absDelta % SECONDS_IN_A_MINUTE);

  return {
    totalHours,
    days,
    hours,
    minutes,
    seconds,
  };
}
